import React from 'react'
import axios from 'axios'
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Button,
  IconButton,
  Chip,
  Typography,
  Grid,
  CircularProgress,
  Collapse,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Search as SearchIcon
} from '@material-ui/icons'

const CATEGORIES = ['All', 'Irrigation', 'Plant', 'Aggregate', 'General']

const coverageChipProps = (status) => {
  switch (status) {
    case 'covered':
      return { label: 'Covered', style: { backgroundColor: '#4caf50', color: '#fff' } }
    case 'partial':
      return { label: 'Partial', style: { backgroundColor: '#ff9800', color: '#fff' } }
    case 'missing':
      return { label: 'Missing', style: { backgroundColor: '#f44336', color: '#fff' } }
    default:
      return { label: status || 'Unknown', color: 'default' }
  }
}

const MaterialCatalogTable = ({ url }) => {
  const [materials, setMaterials] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [categoryFilter, setCategoryFilter] = React.useState('All')
  const [searchTerm, setSearchTerm] = React.useState('')
  const [expandedId, setExpandedId] = React.useState(null)
  const [overrideEditing, setOverrideEditing] = React.useState(null)
  const [overrideValue, setOverrideValue] = React.useState('')
  const [quoteModal, setQuoteModal] = React.useState(null)
  const [quoteData, setQuoteData] = React.useState({ vendor: '', unitCost: '', notes: '' })
  const [deleteTarget, setDeleteTarget] = React.useState(null)

  React.useEffect(() => {
    fetchMaterials()
  }, [categoryFilter])

  const fetchMaterials = () => {
    setLoading(true)
    const params = categoryFilter !== 'All' ? `?category=${categoryFilter.toLowerCase()}` : ''
    axios.get(`${url}/api/bid-estimation/materials${params}`).then(response => {
      setMaterials(response.data)
      setLoading(false)
    }).catch(err => {
      console.error(err)
      setLoading(false)
    })
  }

  const filteredMaterials = React.useMemo(() => {
    if (!searchTerm) return materials
    const term = searchTerm.toLowerCase()
    return materials.filter(m =>
      (m.description || '').toLowerCase().includes(term) ||
      (m.key || '').toLowerCase().includes(term)
    )
  }, [materials, searchTerm])

  const handleToggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id)
  }

  const handleOverrideEdit = (material) => {
    setOverrideEditing(material._id)
    setOverrideValue(material.manualOverride ?? '')
  }

  const handleOverrideSave = (materialId) => {
    const value = overrideValue === '' ? null : Number(overrideValue)
    axios.put(`${url}/api/bid-estimation/materials/${materialId}`, {
      manualOverride: value
    }).then(() => {
      setOverrideEditing(null)
      setOverrideValue('')
      fetchMaterials()
    }).catch(err => {
      console.error(err)
    })
  }

  const handleOverrideCancel = () => {
    setOverrideEditing(null)
    setOverrideValue('')
  }

  const handleAddQuote = () => {
    if (!quoteModal) return
    const payload = {
      vendor: quoteData.vendor,
      unitCost: Number(quoteData.unitCost),
      notes: quoteData.notes
    }
    axios.post(`${url}/api/bid-estimation/materials/${quoteModal}/vendor-quote`, payload).then(() => {
      setQuoteModal(null)
      setQuoteData({ vendor: '', unitCost: '', notes: '' })
      fetchMaterials()
    }).catch(err => {
      console.error(err)
    })
  }

  const handleDelete = () => {
    if (!deleteTarget) return
    axios.delete(`${url}/api/bid-estimation/materials/${deleteTarget._id}`).then(() => {
      setDeleteTarget(null)
      fetchMaterials()
    }).catch(err => {
      console.error(err)
      setDeleteTarget(null)
    })
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <CircularProgress />
      </div>
    )
  }

  return (
    <div>
      <Grid container spacing={2} alignItems="center" style={{ marginBottom: 16 }}>
        <Grid item>
          <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
              label="Category"
            >
              {CATEGORIES.map(c => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search materials..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon style={{ marginRight: 8, color: '#999' }} fontSize="small" />
            }}
          />
        </Grid>
      </Grid>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Description</TableCell>
            <TableCell>Unit</TableCell>
            <TableCell>Category</TableCell>
            <TableCell align="right">Best Price ($)</TableCell>
            <TableCell align="center">Coverage</TableCell>
            <TableCell align="center">Vendors</TableCell>
            <TableCell align="right">Override ($)</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredMaterials.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center">
                <Typography variant="body2" style={{ padding: 20 }}>
                  No materials found.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredMaterials.map(material => {
              const chipProps = coverageChipProps(material.coverageStatus)
              const vendorCount = material.vendorPrices ? material.vendorPrices.length : 0
              const isExpanded = expandedId === material._id
              return (
                <React.Fragment key={material._id}>
                  <TableRow
                    hover
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleToggleExpand(material._id)}
                  >
                    <TableCell>{material.description}</TableCell>
                    <TableCell>{material.unit}</TableCell>
                    <TableCell>
                      <Chip size="small" label={material.category || '—'} variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      {material.bestPrice != null ? `$${Number(material.bestPrice).toFixed(2)}` : '—'}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={chipProps.label}
                        color={chipProps.color || undefined}
                        style={chipProps.style || {}}
                      />
                    </TableCell>
                    <TableCell align="center">{vendorCount}</TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()}>
                      {overrideEditing === material._id ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                          <TextField
                            size="small"
                            type="number"
                            value={overrideValue}
                            onChange={e => setOverrideValue(e.target.value)}
                            style={{ width: 80 }}
                          />
                          <IconButton size="small" onClick={() => handleOverrideSave(material._id)}>
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={handleOverrideCancel}>
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </span>
                      ) : (
                        <span
                          style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                          onClick={() => handleOverrideEdit(material)}
                        >
                          {material.manualOverride != null ? `$${Number(material.manualOverride).toFixed(2)}` : '—'}
                        </span>
                      )}
                    </TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                      <IconButton size="small" onClick={() => setQuoteModal(material._id)}>
                        <AddIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => setDeleteTarget(material)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={8} style={{ paddingBottom: 0, paddingTop: 0 }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <VendorQuotesPanel vendorPrices={material.vendorPrices || []} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              )
            })
          )}
        </TableBody>
      </Table>

      <Dialog open={!!quoteModal} onClose={() => setQuoteModal(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Vendor Quote</DialogTitle>
        <DialogContent>
          <TextField
            label="Vendor"
            value={quoteData.vendor}
            onChange={e => setQuoteData({ ...quoteData, vendor: e.target.value })}
            fullWidth
            margin="normal"
            variant="outlined"
          />
          <TextField
            label="Unit Cost ($)"
            type="number"
            value={quoteData.unitCost}
            onChange={e => setQuoteData({ ...quoteData, unitCost: e.target.value })}
            fullWidth
            margin="normal"
            variant="outlined"
          />
          <TextField
            label="Notes"
            value={quoteData.notes}
            onChange={e => setQuoteData({ ...quoteData, notes: e.target.value })}
            fullWidth
            margin="normal"
            variant="outlined"
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setQuoteModal(null); setQuoteData({ vendor: '', unitCost: '', notes: '' }) }}>
            Cancel
          </Button>
          <Button
            onClick={handleAddQuote}
            variant="contained"
            color="primary"
            disabled={!quoteData.vendor || !quoteData.unitCost}
          >
            Add Quote
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Material</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteTarget?.description}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="secondary" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

const VendorQuotesPanel = ({ vendorPrices }) => {
  if (vendorPrices.length === 0) {
    return (
      <Box py={2} px={1}>
        <Typography variant="body2" color="textSecondary">
          No vendor quotes yet.
        </Typography>
      </Box>
    )
  }

  return (
    <Box py={2} px={1}>
      <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
        Vendor Quotes
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Vendor</TableCell>
            <TableCell align="right">Unit Cost ($)</TableCell>
            <TableCell>Quote Date</TableCell>
            <TableCell>Notes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {vendorPrices.map((vp, index) => (
            <TableRow key={index}>
              <TableCell>{vp.vendor || '—'}</TableCell>
              <TableCell align="right">
                {vp.unitCost != null ? `$${Number(vp.unitCost).toFixed(2)}` : '—'}
              </TableCell>
              <TableCell>
                {vp.quoteDate ? new Date(vp.quoteDate).toLocaleDateString() : '—'}
              </TableCell>
              <TableCell>{vp.notes || '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

export default MaterialCatalogTable
